import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import { useParams } from "react-router-dom";
import IWarehouse_Transfer from "../../../models/IWarehouse_Transfer";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import IOwTableCols from "../../../models/IOwTableCols";
import IWarehouse_Transfer_Catalog_Item_Map from "../../../models/IWarehouse_Transfer_Catalog_Item_Map";
import IGetWarehouseTransferDTO from "../../../models/DTO/IGetWarehouseTransferDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetWarehouseTransferDTO from "../../../models/DTO/IOnGetWarehouseTransferDTO";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import OwTable from "../../shared/OwTable/OwTable";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwButton from "../../shared/OwButton/OwButton";
import IProcessWarehouseTransferItemDTO from "../../../models/DTO/IProcessWarehouseTransferItemDTO";
import IWarehouseTransferMarkReceivedDTO from "../../../models/DTO/IWarehouseTransferMarkReceivedDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import OwLabel from "../../shared/OwLabel/OwLabel";
import UserService from "../../../services/UserService";
import {SocketContext} from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";

type Props = {
};

export const TransferReceived = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    // @ts-expect-error
    const { guid } = useParams();

    const [warehouseTransfer, setWarehouseTransfer] = useState<IWarehouse_Transfer | null>(null);
    const [tableCols, setTableCols] = useState<IOwTableCols[]>([]);
    const [isDataFetching, setIsDataFetching] = useState<boolean>(true);
    const [isInitialData, setIsInitialData] = useState<boolean>(true);
    const [isMarkReceivedActive, setIsMarkReceivedActive] = useState<boolean>(false);
    const [warehouseTransferItems, setWarehouseTransferItems] = useState<IWarehouse_Transfer_Catalog_Item_Map[]>([]);
    const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumbs[]>([
        { title: "inventory", url: "/inventory" },
        { title: "transfers", url: "/inventory/transfers" },
        { title: "transfer", url: `/inventory/transfers/${guid}` },
        { title: "transfer_received", url: `/inventory/transfers/${guid}/received` },
    ]);

    const handleChange = (e: any, row: IWarehouse_Transfer_Catalog_Item_Map) => {
        const value = e.target.value;
        const name = e.target.name;

        const _warehouseTransferItems = [...warehouseTransferItems];

        for (const warehouseTransferItem of _warehouseTransferItems) {
            if (warehouseTransferItem.guid === row.guid) {
                // @ts-expect-error
                warehouseTransferItem[name] = value;
            }
        }

        setWarehouseTransferItems(_warehouseTransferItems);
    };

    const renderReceiveQtyFn = (row: IWarehouse_Transfer_Catalog_Item_Map) => {
        return (
            <OwFnInput
                fn={(e: ChangeEvent<any>) => handleChange(e, row)}
                name={"received_qty"}
                value={row.received_qty || 0}
                disabled={!!warehouseTransfer?.received_by_user_id}
            />
        );
        // return row.received_qty || 0;
    };

    const save = (row: IWarehouse_Transfer_Catalog_Item_Map) => {
        if (warehouseTransfer) {
            const payload: IProcessWarehouseTransferItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                warehouse_transfer_id: warehouseTransfer.id,
                catalog_item_id: row.catalog_item_id,
                received_qty: row.received_qty,
            };

            socket.emit(EndpointsEnum.WarehouseProcessWarehouseTransferItem, payload);
        }
    };

    const renderSaveFn = (row: IWarehouse_Transfer_Catalog_Item_Map) => {
        if (!warehouseTransfer?.received_by_user_id) {
            return <OwButton title={"save"} fn={() => save(row)} />;
        }
    };

    const onProcessWarehouseTransferItem = () => {
        const notification: INotification = {
            message: "successfully_saved",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);
    };

    const getTableCols = (): IOwTableCols[] => {
        return [
            { title: "name", valueProp: "Catalog_Item", valueSubProp: "name" },
            { title: "sku", valueProp: "Catalog_Item", valueSubProp: "sku" },
            { title: "sent_qty", valueProp: "sent_qty" },
            { title: "received_qty", valueProp: "receive_qty", renderFn: renderReceiveQtyFn, renderFnEmitsRow: true, className: "w-10r" },
            { title: "", valueProp: "", renderFn: renderSaveFn, renderFnEmitsRow: true },
        ];
    };

    const getTransfer = () => {
        if (guid) {
            const payload: IGetWarehouseTransferDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                warehouseTransferGuid: guid,
            };

            socket.emit(EndpointsEnum.WarehouseGetTransfer, payload);

            setIsDataFetching(true);
        }
    };

    const onGetTransfer = (data: IOnGetWarehouseTransferDTO) => {
        setWarehouseTransfer(data.warehouseTransfer);
        setWarehouseTransferItems(data.warehouseTransfer.Warehouse_Transfer_Catalog_Items_Maps || []);

        setIsDataFetching(false);
        setIsInitialData(false);
    };

    const markTransferReceived = () => {
        if (guid) {
            const payload: IWarehouseTransferMarkReceivedDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                warehouseTransferGuid: guid,
            };

            socket.emit(EndpointsEnum.WarehouseMarkTransferReceived, payload);
        }
    };

    const onMarkTransferReceived = () => {
        const notification: INotification = {
            message: "successfully_marked_transfer_received",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        setIsMarkReceivedActive(false);
        getTransfer();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.WarehouseOnProcessWarehouseTransferItem, fn: onProcessWarehouseTransferItem },
        { endpoint: EndpointsEnum.WarehouseOnGetTransfer, fn: onGetTransfer },
        { endpoint: EndpointsEnum.WarehouseOnMarkTransferReceived, fn: onMarkTransferReceived },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getTransfer();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        setTableCols(getTableCols());
    }, [warehouseTransferItems]);

    return (
        <div className={"TransferReceived"}>
            <Breadcrumbs crumbs={breadcrumbs} />
            <div className="row">
                <div className="col-12">
                    <h1>Transfer Received</h1>
                </div>
            </div>

            {warehouseTransfer?.received_by_user_id ? (
                <div className={"text-end mb-3"}>
                    <OwLabel title={"received_by"} /> {warehouseTransfer.Received_By?.name} <OwLabel title={"at"} />{" "}
                    {UserService.parseDate(warehouseTransfer.received_at)}
                </div>
            ) : (
                <div className={"mb-2"}>
                    <div className={"text-end"}>
                        <OwButton
                            title={"finalize"}
                            fn={markTransferReceived}
                            active={isMarkReceivedActive}
                            disableOnActive={true}
                            className={"btn-danger"}
                            leftIconClass={"fas fa-lock"}
                        />
                    </div>
                </div>
            )}

            <div>
                <OwTable
                    columns={tableCols}
                    dataSet={warehouseTransferItems}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>
        </div>
    );
};
